import { Badge } from '@evogard/ui';
import { fromColumn, type Money } from '@evogard/domain';

/*
 * As peças de apresentação que as duas abas do Relatório de Vendas dividem —
 * o Painel e a Comparação de meses. Nenhuma conta de negócio mora aqui: são a
 * variação percentual entre dois números que já vieram do servidor, e o jeito de
 * escrevê-los.
 */

/**
 * A variação entre dois números do mesmo recorte. Base zerada não tem
 * variação: "+∞%" seria um número com cara de medido, e a resposta é `null`.
 */
export function variationOf(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

/** O selo "+1,1% cresceu" / "-7,9% caiu" / "0,0% igual". */
export function VariationBadge({ change }: { change: number }) {
  const tone = change > 0 ? 'sucesso' : change < 0 ? 'erro' : 'neutro';
  const word = change > 0 ? 'cresceu' : change < 0 ? 'caiu' : 'igual';
  return (
    <Badge tone={tone}>
      {change > 0 ? '+' : ''}
      {formatPercent(change)} {word}
    </Badge>
  );
}

export function formatPercent(value: number): string {
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/**
 * O eixo em reais redondos, SEM o símbolo — `750 mil`, `1,2 mi`. O eixo tem 64px
 * e "R$ 750 mil" não cabe: o navegador cortava o "R". O título do gráfico diz
 * que é dinheiro, e o valor exato, com o símbolo, é o da dica.
 */
export function compactMoney(cents: number): string {
  return new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(cents / 100);
}

/** O valor em centavos. Ilegível estoura: um gráfico com zero no lugar mentiria. */
export function centsOf(value: string): Money {
  const cents = fromColumn(value);
  if (cents === null) throw new Error(`Valor ilegível no relatório de vendas: ${value}`);
  return cents;
}

export function lastDayOfPeriod(period: string): number {
  const [year, month] = period.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
